/* ARVADA — homepage behaviour: reveal, product rail (Embla), story crossfade slideshow */
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Staggered reveal for everything marked [data-reveal] */
  if (!customElements.get('arvada-home-reveal')) {
    customElements.define('arvada-home-reveal', class extends HTMLElement {
      connectedCallback() {
        // Sections injected after load (e.g. product recommendations) hide their items here instead of inline.
        if (this.hasAttribute('data-reveal-late') && this.dataset.reveal === 'true' && !(window.Shopify && Shopify.designMode) && !reduced()) this.classList.add('avh-js');
        const items = [...this.querySelectorAll('[data-reveal]')];
        if (!this.classList.contains('avh-js') || reduced()) { items.forEach(el => el.classList.add('is-in')); return; }
        let queue = [], timer = null;
        const flush = () => {
          queue.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1)
            .forEach((el, i) => { el.style.setProperty('--avh-delay', `${Math.min(i, 6) * 90}ms`); el.classList.add('is-in'); });
          queue = []; timer = null;
        };
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.observer.unobserve(entry.target); queue.push(entry.target);
          });
          if (queue.length && !timer) timer = requestAnimationFrame(flush);
        }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
        items.forEach(el => this.observer.observe(el));
        this.addEventListener('focusin', e => e.target.closest('[data-reveal]')?.classList.add('is-in'));
      }
      disconnectedCallback() { this.observer?.disconnect(); }
    });
  }

  /* Product rail — Embla, paged by visible group, progress thumb */
  if (!customElements.get('arvada-product-rail')) {
    customElements.define('arvada-product-rail', class extends HTMLElement {
      connectedCallback() {
        if (typeof EmblaCarousel === 'function') this.init();
        else window.addEventListener('load', () => this.init(), { once: true });
      }
      init() {
        if (this.api || !this.isConnected || typeof EmblaCarousel !== 'function') return;
        const root = this.closest('.avh') || this;
        this.viewport = this.querySelector('[data-viewport]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        if (!this.viewport || this.slides.length < 2) return;
        this.prev = root.querySelector('[data-prev]');
        this.next = root.querySelector('[data-next]');
        this.thumb = this.querySelector('[data-progress]');
        this.pauseBtn = this.querySelector('[data-pause]');
        this.ctrl = new AbortController();
        const on = { signal: this.ctrl.signal };
        const autoplayOn = this.dataset.autoplay === 'true' && typeof EmblaCarouselAutoplay === 'function' && !reduced();
        this.auto = autoplayOn ? EmblaCarouselAutoplay({ delay: (Number(this.dataset.interval) || 5) * 1000, stopOnInteraction: false, stopOnMouseEnter: true, stopOnFocusIn: true, playOnInit: false }) : null;
        this.dataset.ready = 'true';
        this.api = EmblaCarousel(this.viewport, {
          align: 'start', containScroll: 'trimSnaps', slidesToScroll: 'auto', loop: false, skipSnaps: false,
          duration: reduced() ? 0 : Math.max(18, Math.min(50, (Number(this.dataset.duration) || 500) / 16))
        }, this.auto ? [this.auto] : []);
        const update = () => this.update();
        this.api.on('select', update).on('reInit', update).on('scroll', () => this.paint()).on('resize', update);
        this.prev?.addEventListener('click', () => { this.api.scrollPrev(); this.auto?.reset(); }, on);
        this.next?.addEventListener('click', () => { this.api.scrollNext(); this.auto?.reset(); }, on);
        this.viewport.addEventListener('keydown', e => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.api.scrollNext(); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.api.scrollPrev(); }
        }, on);
        this.pauseBtn?.addEventListener('click', () => {
          this.paused = !this.paused; this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
          this.pauseBtn.setAttribute('aria-label', this.paused ? this.pauseBtn.dataset.playLabel : this.pauseBtn.dataset.pauseLabel);
          this.syncAuto();
        }, on);
        if (this.auto) {
          this.io = new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; this.syncAuto(); }, { threshold: 0.25 });
          this.io.observe(this);
          document.addEventListener('visibilitychange', () => this.syncAuto(), on);
        }
        this.update();
      }
      syncAuto() {
        if (!this.auto) return;
        const go = this.visible && !document.hidden && !this.paused && this.overflow;
        if (go && !this.auto.isPlaying()) this.auto.play(); else if (!go && this.auto.isPlaying()) this.auto.stop();
      }
      update() {
        const snaps = this.api.scrollSnapList().length;
        this.overflow = snaps > 1;
        this.toggleAttribute('data-static', !this.overflow);
        if (this.prev) { this.prev.disabled = !this.api.canScrollPrev(); this.prev.hidden = !this.overflow; }
        if (this.next) { this.next.disabled = !this.api.canScrollNext(); this.next.hidden = !this.overflow; }
        if (this.thumb) this.thumb.parentElement.hidden = !this.overflow;
        if (this.pauseBtn) this.pauseBtn.hidden = !this.auto || !this.overflow;
        this.viewport.tabIndex = this.overflow ? 0 : -1;
        this.paint(); this.syncAuto();
      }
      paint() {
        if (!this.thumb || !this.api) return;
        const size = 100 / Math.max(1, this.api.scrollSnapList().length);
        const progress = Math.max(0, Math.min(1, this.api.scrollProgress()));
        this.thumb.style.setProperty('--avh-thumb', `${size}%`);
        this.thumb.style.setProperty('--avh-thumb-x', `${progress * (100 / size * 100 - 100)}%`);
      }
      disconnectedCallback() { this.ctrl?.abort(); this.io?.disconnect(); this.api?.destroy(); this.api = null; delete this.dataset.ready; }
    });
  }

  /* Story slideshow — crossfade, swipe, dots, pause */
  if (!customElements.get('arvada-story-slides')) {
    customElements.define('arvada-story-slides', class extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.index = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
        this.controls = this.querySelector('[data-controls]');
        if (this.slides.length < 2) { if (this.controls) this.controls.hidden = true; return; }
        this.ctrl = new AbortController(); const on = { signal: this.ctrl.signal };
        this.dots = this.querySelector('[data-dots]');
        this.pauseBtn = this.querySelector('[data-pause]');
        this.status = this.querySelector('[data-status]');
        this.autoplay = this.dataset.autoplay === 'true' && !reduced();
        this.delay = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
        this.controls.hidden = false;
        this.slides.forEach((_, i) => {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'avh-dot'; b.setAttribute('aria-label', `${this.dataset.label || 'Slide'} ${i + 1}`);
          b.addEventListener('click', () => { this.go(i); this.restart(); }, on);
          this.dots.appendChild(b);
        });
        if (this.pauseBtn) {
          this.pauseBtn.hidden = !this.autoplay;
          this.pauseBtn.addEventListener('click', () => {
            this.paused = !this.paused; this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
            this.pauseBtn.setAttribute('aria-label', this.paused ? this.pauseBtn.dataset.playLabel : this.pauseBtn.dataset.pauseLabel);
            this.restart();
          }, on);
        }
        let x0 = null, y0 = null;
        this.addEventListener('pointerdown', e => { if (e.target.closest('button')) return; x0 = e.clientX; y0 = e.clientY; }, on);
        this.addEventListener('pointerup', e => {
          if (x0 === null) return; const dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
        }, on);
        this.addEventListener('pointercancel', () => { x0 = null; }, on);
        this.addEventListener('keydown', e => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); this.restart(); }
        }, on);
        this.addEventListener('mouseenter', () => { this.isHovered = true; this.restart(); }, on);
        this.addEventListener('mouseleave', () => { this.isHovered = false; this.restart(); }, on);
        this.addEventListener('focusin', () => { this.hasFocus = true; this.restart(); }, on);
        this.addEventListener('focusout', e => { if (!this.contains(e.relatedTarget)) { this.hasFocus = false; this.restart(); } }, on);
        document.addEventListener('visibilitychange', () => this.restart(), on);
        this.addEventListener('shopify:block:select', e => { const i = this.slides.indexOf(e.target.closest('[data-slide]')); if (i > -1) { this.editing = true; this.go(i); this.restart(); } }, on);
        this.addEventListener('shopify:block:deselect', () => { this.editing = false; this.restart(); }, on);
        this.io = new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; this.restart(); }, { threshold: 0.2 });
        this.io.observe(this);
        this.go(this.index, true);
      }
      go(i, silent) {
        const n = this.slides.length; this.index = (i + n) % n;
        this.slides.forEach((s, k) => { const on = k === this.index; s.classList.toggle('is-active', on); s.setAttribute('aria-hidden', String(!on)); });
        [...this.dots.children].forEach((d, k) => d.setAttribute('aria-current', String(k === this.index)));
        if (!silent && this.status) this.status.textContent = `${this.index + 1} / ${n}`;
      }
      restart() {
        clearTimeout(this.timer);
        if (this.autoplay && this.visible && !this.paused && !this.isHovered && !this.hasFocus && !this.editing && !document.hidden) {
          this.timer = setTimeout(() => { this.go(this.index + 1, true); this.restart(); }, this.delay);
        }
      }
      disconnectedCallback() { clearTimeout(this.timer); this.ctrl?.abort(); this.io?.disconnect(); }
    });
  }
})();
